// Single source of truth for which files agents may attach to tickets (sidebar
// upload zone AND the reply composer paperclip). The browser's `accept` attribute
// only filters the file-picker dialog — drag-drop, the composer paperclip, and
// programmatic adds bypass it — so callers must run validateAttachmentFiles()
// in app code. The file-service enforces a server-side blocklist as the final
// backstop, since any client check can be bypassed entirely.

// Allowed types, in <input accept> syntax (extensions, wildcard MIME, exact MIME).
export const ATTACHMENT_ACCEPT =
  'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip';

// Default per-file size cap (10 MB).
export const ATTACHMENT_MAX_SIZE = 10 * 1024 * 1024;

function formatMb(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

// True when the file matches the accept whitelist by extension OR MIME type.
// Returns true when no real whitelist is set (e.g. "*/*").
export function isFileTypeAccepted(
  file: File,
  accept: string = ATTACHMENT_ACCEPT,
): boolean {
  const normalized = accept?.trim().toLowerCase();
  if (!normalized || normalized === '*/*' || normalized === '*') return true;

  const tokens = normalized.split(',').map((t) => t.trim()).filter(Boolean);
  const fileName = file.name.toLowerCase();
  const mimeType = (file.type || '').toLowerCase();

  return tokens.some((token) => {
    if (token.startsWith('.')) return fileName.endsWith(token);
    if (token.endsWith('/*')) {
      const group = token.slice(0, token.indexOf('/'));
      return mimeType.startsWith(`${group}/`);
    }
    return mimeType === token;
  });
}

export interface AttachmentValidationOptions {
  accept?: string;
  maxSize?: number;
  maxFiles?: number;
}

export interface AttachmentValidationResult {
  valid: File[];
  errors: string[];
}

// Filters a file list down to the allowed ones, collecting a human-readable
// reason for each rejected file. Enforces type, size and (optionally) count.
export function validateAttachmentFiles(
  files: File[],
  options: AttachmentValidationOptions = {},
): AttachmentValidationResult {
  const accept = options.accept ?? ATTACHMENT_ACCEPT;
  const maxSize = options.maxSize ?? ATTACHMENT_MAX_SIZE;
  const { maxFiles } = options;

  const errors: string[] = [];
  let candidates = files;

  if (maxFiles && candidates.length > maxFiles) {
    errors.push(`Maximum ${maxFiles} files allowed`);
    candidates = candidates.slice(0, maxFiles);
  }

  const valid = candidates.filter((file) => {
    if (!isFileTypeAccepted(file, accept)) {
      errors.push(`File "${file.name}" type is not allowed`);
      return false;
    }
    if (file.size > maxSize) {
      errors.push(`File "${file.name}" exceeds the ${formatMb(maxSize)} limit`);
      return false;
    }
    return true;
  });

  return { valid, errors };
}
